import * as z from 'zod';
// Public fields only — safe to expose to end users via client-api.
export const PublicImageRefSchema = z.object({
    src: z.url(),
    alt: z.string(),
});
// Admin/Firestore shape — adds the kaleido `imageId` and crop params so the
// inline cropper can be re-mounted. These fields should NOT flow to client-api.
export const ImageRefSchema = PublicImageRefSchema.extend({
    imageId: z.string().optional(), // Kaleido ID (for cropper/derivates)
    bildebankenId: z.string().optional(), // Bildebanken ID (for detail view)
    crop: z
        .strictObject({
        x: z.number().min(0).max(1),
        y: z.number().min(0).max(1),
        w: z.number().min(0).max(1),
        h: z.number().min(0).max(1),
    })
        .optional(),
});
const BaseQuestionSchema = z.strictObject({
    id: z.uuidv7(),
    tournamentId: z.uuidv7(),
    roundId: z.uuidv7(),
    text: z.string().min(1),
    points: z.number().int().nonnegative(),
    createdAt: z.number().int().nonnegative(),
    updatedAt: z.number().int().nonnegative(),
    autogenerated: z.boolean().optional(),
    mainImage: ImageRefSchema.optional(),
    isWidgetEligible: z.boolean().optional(),
});
export const SingleChoiceQuestionSchema = BaseQuestionSchema.extend({
    type: z.literal('SINGLE_CHOICE'),
    component: z.enum(['CHOICES', 'CORNER_KICK']),
    choices: z
        .array(z.strictObject({
        id: z.uuidv7(),
        text: z.string(),
        image: ImageRefSchema.optional(),
    }))
        .min(2)
        .max(10),
});
export const MultipleNumbersQuestionSchema = BaseQuestionSchema.extend({
    type: z.literal('MULTIPLE_NUMBERS'),
    component: z.enum(['MATCH_WINNER_PICKER']),
    choices: z
        .array(z.strictObject({
        id: z.uuidv7(),
        text: z.string(),
        image: ImageRefSchema.optional(),
        minValue: z.number().int().nonnegative(),
        maxValue: z.number().int().nonnegative(),
        unit: z.string().optional(),
    }))
        .min(2)
        .max(5),
});
export const NumberGuessQuestionBaseSchema = BaseQuestionSchema.extend({
    type: z.literal('NUMBER_GUESS'),
    component: z.enum(['NUMBER_SLIDER', 'STOPWATCH', 'CARD_COUNTER']),
    minValue: z.number().int().nonnegative(),
    maxValue: z.number().int().nonnegative(),
    unit: z.string().optional(),
    step: z.number().int().positive().optional(),
    alternativeAnswer: z
        .strictObject({
        enabled: z.boolean(),
        text: z.string().min(1),
    })
        .optional(),
});
export const NumberGuessQuestionSchema = NumberGuessQuestionBaseSchema.refine((data) => !data.alternativeAnswer?.enabled || data.component === 'STOPWATCH', {
    message: 'Alternative answer only supported for STOPWATCH component',
});
export const RankedQuestionSchema = BaseQuestionSchema.extend({
    type: z.literal('RANKED'),
    component: z.enum(['PODIUM', 'DRAG_SORT_LIST']),
    alternatives: z
        .array(z.strictObject({
        id: z.uuidv7(),
        text: z.string(),
        image: z.string().optional(),
    }))
        .min(2)
        .max(5),
});
export const CoordinateQuestionSchema = BaseQuestionSchema.extend({
    type: z.literal('COORDINATE'),
    component: z.enum(['GOAL', 'FIELD']),
    aspectRatio: z.number().multipleOf(0.01).positive().meta({
        description: 'Width / height ratio of the physical object (e.g. 3.0 for a goal). Scales x distances relative to y when computing how far off a coordinate guess is',
    }),
    alternativeAnswer: z
        .strictObject({
        enabled: z.boolean(),
        text: z.string().min(1),
    })
        .optional(),
});
export const QuestionSchema = z.discriminatedUnion('type', [
    SingleChoiceQuestionSchema,
    MultipleNumbersQuestionSchema,
    NumberGuessQuestionSchema,
    RankedQuestionSchema,
    CoordinateQuestionSchema,
]);
const BaseAnswerSchema = z.strictObject({
    id: z.uuidv7(),
    tournamentId: z.uuidv7(),
    roundId: z.uuidv7(),
    questionId: z.uuidv7(),
    isLate: z.boolean(),
    createdAt: z.number().int().nonnegative(),
    updatedAt: z.number().int().nonnegative(),
});
export const SingleChoiceAnswerSchema = BaseAnswerSchema.extend({
    type: z.literal('SINGLE_CHOICE'),
    alternativeId: z.uuidv7(),
});
export const MultipleNumbersAnswerSchema = BaseAnswerSchema.extend({
    type: z.literal('MULTIPLE_NUMBERS'),
    values: z.record(z.uuidv7(), z.number().int().nonnegative()),
});
export const NumberGuessAnswerBaseSchema = BaseAnswerSchema.extend({
    type: z.literal('NUMBER_GUESS'),
    value: z.number().int().nonnegative().optional(),
    isAlternativeAnswer: z.boolean().optional(),
});
export const NumberGuessAnswerSchema = NumberGuessAnswerBaseSchema.refine((data) => {
    if (data.isAlternativeAnswer) {
        return data.value === undefined;
    }
    return data.value !== undefined;
}, {
    message: 'value must be absent when isAlternativeAnswer is true, and present when false',
});
export const RankedAnswerSchema = BaseAnswerSchema.extend({
    type: z.literal('RANKED'),
    orderedIds: z.array(z.uuidv7()).min(1).max(5),
});
export const CoordinateAnswerBaseSchema = BaseAnswerSchema.extend({
    type: z.literal('COORDINATE'),
    x: z.number().multipleOf(0.001).min(0).max(1).optional(),
    y: z.number().multipleOf(0.001).min(0).max(1).optional(),
    isAlternativeAnswer: z.boolean().optional(),
});
export const CoordinateAnswerSchema = CoordinateAnswerBaseSchema.refine((data) => {
    if (data.isAlternativeAnswer) {
        return data.x === undefined && data.y === undefined;
    }
    return data.x !== undefined && data.y !== undefined;
}, {
    message: 'x and y must be absent when isAlternativeAnswer is true, and present when false',
});
export const AnswerSchema = z.discriminatedUnion('type', [
    SingleChoiceAnswerSchema,
    MultipleNumbersAnswerSchema,
    NumberGuessAnswerSchema,
    RankedAnswerSchema,
    CoordinateAnswerSchema,
]);
const BaseSolutionSchema = z.strictObject({
    id: z.uuidv7().meta({
        description: 'The solution ID, which is the same as the ID of the question it belongs to',
    }),
    tournamentId: z.uuidv7(),
    roundId: z.uuidv7(),
    createdAt: z.number().int().nonnegative(),
    updatedAt: z.number().int().nonnegative(),
});
export const SingleChoiceSolutionSchema = BaseSolutionSchema.extend({
    type: z.literal('SINGLE_CHOICE'),
    correctAlternativeId: z.uuidv7(),
});
export const MultipleNumbersSolutionSchema = BaseSolutionSchema.extend({
    type: z.literal('MULTIPLE_NUMBERS'),
    correctValues: z.record(z.uuidv7(), z.number().int().nonnegative()),
});
export const NumberGuessSolutionBaseSchema = BaseSolutionSchema.extend({
    type: z.literal('NUMBER_GUESS'),
    correctValue: z.number().int().nonnegative().optional(),
    alternativeIsCorrect: z.boolean().optional(),
});
export const NumberGuessSolutionSchema = NumberGuessSolutionBaseSchema.refine((data) => {
    if (data.alternativeIsCorrect) {
        return data.correctValue === undefined;
    }
    return data.correctValue !== undefined;
}, {
    message: 'correctValue must be absent when alternativeIsCorrect is true, and present when false',
});
export const RankedSolutionSchema = BaseSolutionSchema.extend({
    type: z.literal('RANKED'),
    correctOrder: z.array(z.uuidv7()),
});
export const CoordinateSolutionBaseSchema = BaseSolutionSchema.extend({
    type: z.literal('COORDINATE'),
    correctX: z.number().multipleOf(0.001).min(0).max(1).optional(),
    correctY: z.number().multipleOf(0.001).min(0).max(1).optional(),
    alternativeIsCorrect: z.boolean().optional(),
    scoringParams: z
        .strictObject({
        tolerance: z.number().positive(),
        threshold: z.number().positive(),
        decay: z.number().positive(),
    })
        .optional()
        .meta({
        description: 'Scoring parameters used when this solution was created. Only present for wrapped data to render the score aura correctly.',
    }),
});
export const CoordinateSolutionSchema = CoordinateSolutionBaseSchema.refine((data) => {
    if (data.alternativeIsCorrect) {
        return data.correctX === undefined && data.correctY === undefined;
    }
    return data.correctX !== undefined && data.correctY !== undefined;
}, {
    message: 'correctX and correctY must be absent when alternativeIsCorrect is true, and present when false',
});
export const SolutionSchema = z.discriminatedUnion('type', [
    SingleChoiceSolutionSchema,
    MultipleNumbersSolutionSchema,
    NumberGuessSolutionSchema,
    RankedSolutionSchema,
    CoordinateSolutionSchema,
]);
const BaseAnswerStatsSchema = z.strictObject({
    questionId: z.uuidv7(),
    refreshedAt: z.number().int().nonnegative(),
});
export const SingleChoiceAnswerStatsSchema = BaseAnswerStatsSchema.extend({
    type: z.literal('SINGLE_CHOICE'),
    choicePercents: z
        .record(z.uuidv7(), z.number().multipleOf(0.01).min(0).max(1))
        .meta({
        description: 'Will only include choices that have received at least one vote. The other choices will be omitted here.',
    }),
});
export const MultipleNumbersAnswerStatsSchema = BaseAnswerStatsSchema.extend({
    type: z.literal('MULTIPLE_NUMBERS'),
    modes: z.record(z.uuidv7(), z.number().int().nonnegative()),
});
export const NumberGuessAnswerStatsSchema = BaseAnswerStatsSchema.extend({
    type: z.literal('NUMBER_GUESS'),
    mode: z.number().int().nonnegative().nullable(),
    alternativePercentage: z.number().multipleOf(0.01).min(0).max(1).optional(),
});
export const RankedAnswerStatsSchema = BaseAnswerStatsSchema.extend({
    type: z.literal('RANKED'),
    mode: z.array(z.uuidv7()).meta({
        description: 'The most common ordering of the IDs',
    }),
});
export const CoordinateAnswerStatsSchema = BaseAnswerStatsSchema.extend({
    type: z.literal('COORDINATE'),
    heatmap: z.strictObject({
        resolution: z
            .number()
            .int()
            .positive()
            .meta({ description: 'Number of cells is resolution x resolution' }),
        cells: z
            .array(z.strictObject({
            col: z.number().int().nonnegative(),
            row: z.number().int().nonnegative(),
            count: z.number().int().positive(),
        }))
            .meta({ description: 'Sparse matrix including only non-zero cells' }),
    }),
    alternativePercentage: z.number().multipleOf(0.01).min(0).max(1).optional(),
});
export const AnswerStatsSchema = z.discriminatedUnion('type', [
    SingleChoiceAnswerStatsSchema,
    MultipleNumbersAnswerStatsSchema,
    NumberGuessAnswerStatsSchema,
    RankedAnswerStatsSchema,
    CoordinateAnswerStatsSchema,
]);
//# sourceMappingURL=quiz.js.map